import React, { useState, useEffect, useRef, useCallback } from "react";
import "./ZoneSettings.css";
import i18n from "../../i18n";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { MdSave } from "react-icons/md";
import {
  fetchAllQuestions,
  fetchTempZoneSettings,
  saveZoneSettings,
  tempSaveZoneSettings,
} from "../../services/zones.services";
import { useDispatch } from "react-redux";
import { showSnackBar } from "../../store/common/snackBarSlice";
import QuestionsCard from "./Components/QuestionsCard";
import SettingsCard from "./Components/SettingsCard";
import { schema } from "./Components/constants";
import Loader from "./Components/Loader";
import Zones from "./Components/Zones";
import { activeQuestions } from "../../constants/zones";
import { useTranslation } from "react-i18next";

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}));

const ZoneSettings = ({
  zones,
  newZoneAdded,
  setNewZoneAdded,
  zoneDeleted,
  setZoneDeleted,
  reloadMap,
}) => {
  const { t } = useTranslation("carrier");
  const [expandDropdown, setExpandDropdown] = useState();
  const [rangeDropdown, setRangeDropdown] = useState();
  const [settingsLoader, setSettingsLoader] = useState(false);
  const [data, setData] = useState(() => schema.settings);
  const [focused, setFocused] = useState("");
  const [zonePerPage, setZonePerPage] = useState(2);
  const [questions, setQuestions] = useState([]);
  const [loadingField, setLoadingField] = useState(false);
  const [activeQuestion, setActiveQuestion] = useState();
  const [hoverQuestion, setHoverQuestion] = useState();
  const [sync, setSync] = useState();
  const [rangeData, setRangeData] = useState(() => [
    {
      min: 0,
      max: 15,
      zoneData: zones.map((item) => ({
        zone_id: item.id,
        price: "",
        enabled: false,
        is_active: 0,
      })),
    },
  ]);
  const [dimensions, setDimensions] = useState(() => [
    {
      min_length: 1,
      min_width: 1,
      min_height: 1,
      max_length: 5,
      max_width: 5,
      max_height: 5,
      zoneData: zones.map((item) => ({
        zone_id: item.id,
        price: "",
        enabled: false,
        is_active: 0,
      })),
    },
  ]);
  const [cod, setCod] = useState(() => [
    {
      min: 0,
      max: 15,
      zoneData: zones.map((item) => ({
        zone_id: item.id,
        price: "",
        enabled: false,
        is_active: 0,
      })),
    },
  ]);
  const [protection, setProtection] = useState(() => [
    {
      min: 0,
      max: 15,
      zoneData: zones.map((item) => ({
        zone_id: item.id,
        price: "",
        enabled: false,
        is_active: 0,
      })),
    },
  ]);

  const dispatch = useDispatch();
  const isInitialRender = useRef(true);

  const getAllQuestions = async () => {
    setSettingsLoader(i18n.t("carrier:zoneSettings.fetchingData"));
    isInitialRender.current = true;
    const response = await fetchAllQuestions();
    if (response.data) {
      const sortedQuestions = [...response.data.result].sort((a, b) => {
        const aIsActive = activeQuestions.includes(a.id) ? -1 : 1;
        const bIsActive = activeQuestions.includes(b.id) ? -1 : 1;
        return aIsActive - bIsActive;
      });
      setQuestions(sortedQuestions);
    }
    setSettingsLoader(false);
  };

  // Utility to normalize zoneData arrays to match zones
  function normalizeZoneDataArray(dataArray, zones, unit) {
    if (!Array.isArray(dataArray)) return [];
    return dataArray.map((row) => ({
      ...row,
      zoneData: zones.map((zone) => {
        const found = row.zoneData?.find(
          (z) => String(z.zone_id) === String(zone.id)
        );
        return found
          ? {
              ...found,
              enabled:
                found.enabled === "1" ||
                found.enabled === 1 ||
                found.enabled === true,
              is_active:
                found.is_active === "1" ||
                found.is_active === 1 ||
                found.is_active === true,
              unit: found.unit || unit,
            }
          : {
              zone_id: zone.id,
              enabled: false,
              is_active: 0,
              price: "",
              unit: unit,
            };
      }),
    }));
  }

  // Fetch temporary settings
  const getTempSettings = async () => {
    setSettingsLoader(i18n.t("carrier:zoneSettings.generatingTable"));
    isInitialRender.current = true;
    const response = await fetchTempZoneSettings();
    if (!response.error) {
      if (response.data?.result?.settings?.length > 0) {
        const res = response.data.result.settings;
        setSync(response.data.result.sync);
        setData(res);
        const rangeItem = res?.find(
          (item) => String(item.question_id) === String(4)
        );
        const dimensionItem = res?.find(
          (item) => String(item.question_id) === String(5)
        );
        const codItem = res?.find(
          (item) => String(item.question_id) === String(6)
        );
        const protectionItem = res?.find(
          (item) => String(item.question_id) === String(20)
        );
        let normalizedRangeData = [];
        let normalizedDimensions = [];
        if (rangeItem?.rangeData) {
          normalizedRangeData = normalizeZoneDataArray(
            rangeItem?.rangeData,
            zones,
            rangeItem?.unit
          );
          setRangeData(normalizedRangeData);
        }
        if (dimensionItem?.dimensions) {
          normalizedDimensions = normalizeZoneDataArray(
            dimensionItem?.dimensions,
            zones,
            dimensionItem?.unit
          );
          setDimensions(normalizedDimensions);
        }
        if (codItem?.cod) {
          setCod(normalizeZoneDataArray(codItem.cod, zones, codItem.unit));
        }
        if (protectionItem?.shipmentProtectionRange) {
          setProtection(
            normalizeZoneDataArray(
              protectionItem?.shipmentProtectionRange,
              zones,
              protectionItem?.unit
            )
          );
        }

        setData((prevData) =>
          prevData.map((item) => {
            if (String(item.question_id) === "4") {
              // For weight questions, ensure at least one range item
              const rangeDataToUse =
                normalizedRangeData.length > 0
                  ? normalizedRangeData
                  : [
                      {
                        min: 0,
                        max: 15,
                        zoneData: zones.map((zone) => ({
                          zone_id: zone.id,
                          price: "",
                          enabled: false,
                          is_active: 0,
                        })),
                      },
                    ];
              return { ...item, rangeData: rangeDataToUse };
            }
            if (String(item.question_id) === "5") {
              // For size questions, ensure at least one dimension item
              const dimensionsToUse =
                normalizedDimensions.length > 0
                  ? normalizedDimensions
                  : [
                      {
                        min_length: 1,
                        min_width: 1,
                        min_height: 1,
                        max_length: 5,
                        max_width: 5,
                        max_height: 5,
                        zoneData: zones.map((zone) => ({
                          zone_id: zone.id,
                          price: "",
                          enabled: false,
                          is_active: 0,
                        })),
                      },
                    ];
              return { ...item, dimensions: dimensionsToUse };
            }
            return item;
          })
        );
      } else {
        tableSetup();
      }
    } else {
      dispatch(
        showSnackBar({
          text: t("zoneSettings.couldntFetchSavedSettings"),
          severity: "error",
        })
      );
    }
    setSettingsLoader(false);
  };

  const handleTempSave = useCallback(
    async (syncValue) => {
      if (questions.length === 0 || questions.length !== data.length) return;

      const settings = data.map((item, index) => {
        const questionType = questions?.find(
          (quest) => `${item.question_id}` === `${quest.id}`
        )?.type;
        if (questionType === "weight") {
          const updated = (item.rangeData || []).map((itm) => ({
            ...itm,
            zoneData: (itm.zoneData || [])
              .filter((zd) =>
                zones.some((zn) => String(zn.id) === String(zd.zone_id))
              )
              .map((zd) => ({
                ...zd,
                unit: item.unit,
                is_active: zd.enabled ? 1 : 0,
              })),
          }));
          return { ...item, rangeData: updated };
        }
        if (questionType === "size") {
          const updated = (item.dimensions || []).map((itm) => ({
            ...itm,
            zoneData: (itm.zoneData || [])
              .filter((zd) =>
                zones.some((zn) => String(zn.id) === String(zd.zone_id))
              )
              .map((zd) => ({
                ...zd,
                unit: item.unit,
                is_active: zd.enabled ? 1 : 0,
              })),
          }));
          return { ...item, dimensions: updated };
        }
        if (questionType === "cod") {
          const updated = (item.cod || []).map((itm) => ({
            ...itm,
            zoneData: (itm.zoneData || [])
              .filter((zd) =>
                zones.some((zn) => String(zn.id) === String(zd.zone_id))
              )
              .map((zd) => ({
                ...zd,
                unit: item.unit,
                is_active: zd.enabled ? 1 : 0,
              })),
          }));
          return { ...item, cod: updated };
        }
        if (questionType === "protection") {
          const updated = (item.shipmentProtectionRange || []).map((itm) => ({
            ...itm,
            zoneData: (itm.zoneData || [])
              .filter((zd) =>
                zones.some((zn) => String(zn.id) === String(zd.zone_id))
              )
              .map((zd) => ({
                ...zd,
                unit: item.unit,
                is_active: zd.enabled ? 1 : 0,
              })),
          }));
          return { ...item, shipmentProtectionRange: updated };
        }
        if (["simple", "number", "time_range"].includes(questionType)) {
          const zoneData = (item.zoneData || [])
            .filter((zd) =>
              zones.some((zn) => String(zn.id) === String(zd.zone_id))
            )
            .map((zd) => ({
              ...zd,
              unit: item.unit,
              is_active: zd.enabled ? 1 : 0,
            }));
          return { ...item, zoneData };
        }
        return item;
      });

      const response = await tempSaveZoneSettings({
        settings,
        sync: syncValue ?? false,
      });
      if (!response.error && syncValue === true) {
        setSync(true);
      }
      setLoadingField(false);
    },
    [data, questions, zones]
  );

  // Save settings permanently to database
  const handleSaveZoneSettings = async () => {
    setSettingsLoader(i18n.t("carrier:zoneSettings.saving"));
    const settings = data.map((item, index) => {
      const questionType = questions?.find(
        (quest) => `${item.question_id}` === `${quest.id}`
      )?.type;
      if (questionType === "weight") {
        const updated = (item.rangeData || []).map((itm) => ({
          ...itm,
          zone_data: (itm.zoneData || []).map((zd) => ({
            ...zd,
            unit: item.unit,
            is_active: zd.enabled ? 1 : 0,
          })),
        }));
        return { ...item, range_data: updated, dimensions: [] };
      }
      if (questionType === "size") {
        const updated = (item.dimensions || []).map((itm) => ({
          ...itm,
          zone_data: (itm.zoneData || []).map((zd) => ({
            ...zd,
            unit: item.unit,
            is_active: zd.enabled ? 1 : 0,
          })),
        }));
        return { ...item, dimensions: updated, range_data: [] };
      }
      if (questionType === "cod") {
        const updated = (item.cod || []).map((itm) => ({
          ...itm,
          zone_data: (itm.zoneData || []).map((zd) => ({
            ...zd,
            unit: item.unit,
            is_active: zd.enabled ? 1 : 0,
          })),
        }));
        return { ...item, range_data: updated, dimensions: [] };
      }
      if (questionType === "protection") {
        const updated = (item.shipmentProtectionRange || []).map((itm) => ({
          ...itm,
          zone_data: (itm.zoneData || []).map((zd) => ({
            ...zd,
            unit: item.unit,
            is_active: zd.enabled ? 1 : 0,
          })),
        }));
        return { ...item, range_data: updated, dimensions: [] };
      }
      if (["simple", "number", "time_range"].includes(questionType)) {
        const zoneData = (item.zoneData || []).map((zd) => ({
          ...zd,
          unit: item.unit,
          is_active: zd.enabled ? 1 : 0,
        }));
        return { ...item, zone_data: zoneData, dimensions: [], range_data: [] };
      }
      return item;
    });

    const response = await saveZoneSettings({ settings });
    if (!response.error) {
      dispatch(
        showSnackBar({
          text: t("zoneSettings.zoneSavedSuccessfully"),
          severity: "success",
        })
      );
      setSync(true);
      await handleTempSave(true);
    } else {
      dispatch(
        showSnackBar({
          text: t("zoneSettings.couldntSaveZone"),
          severity: "error",
        })
      );
    }
    setSettingsLoader(false);
  };

  // Setup table if no temp settings exist
  const tableSetup = () => {
    setData(
      questions.map((quest) => {
        const baseData = {
          question_id: quest.id,
          unit: quest.units[0],
          type: [1, 3, 7, 16].includes(quest.id) ? "shipment" : "package",
          cod,
          shipmentProtectionRange: protection,
          zoneData: zones.map((zone) => ({
            price: "",
            zone_id: zone.id,
            enabled: false,
            is_active: 0,
          })),
        };

        // For weight questions, ensure at least one range item
        if (quest.type === "weight") {
          const rangeDataToUse =
            rangeData.length > 0
              ? rangeData
              : [
                  {
                    min: 0,
                    max: 15,
                    zoneData: zones.map((zone) => ({
                      zone_id: zone.id,
                      price: "",
                      enabled: false,
                      is_active: 0,
                    })),
                  },
                ];
          return { ...baseData, rangeData: rangeDataToUse, dimensions: [] };
        }

        // For size questions, ensure at least one dimension item
        if (quest.type === "size") {
          const dimensionsToUse =
            dimensions.length > 0
              ? dimensions
              : [
                  {
                    min_length: 1,
                    min_width: 1,
                    min_height: 1,
                    max_length: 5,
                    max_width: 5,
                    max_height: 5,
                    zoneData: zones.map((zone) => ({
                      zone_id: zone.id,
                      price: "",
                      enabled: false,
                      is_active: 0,
                    })),
                  },
                ];
          return { ...baseData, dimensions: dimensionsToUse, rangeData: [] };
        }

        // For other question types
        return { ...baseData, rangeData: [], dimensions: [] };
      })
    );
  };

  // Add new zone and update sync
  const addNewZone = async (zone) => {
    setData((prevData) =>
      prevData.map((quest, index) => {
        const questionType = questions[index]?.type;
        if (questionType === "simple") {
          return {
            ...quest,
            zoneData: [
              ...quest.zoneData,
              { price: "", zone_id: zone.id, enabled: false, is_active: 0 },
            ],
          };
        }
        if (questionType === "number") {
          return {
            ...quest,
            zoneData: [
              ...quest.zoneData,
              {
                transit_days: "",
                zone_id: zone.id,
                enabled: false,
                is_active: 0,
              },
            ],
          };
        }
        if (questionType === "time_range") {
          return {
            ...quest,
            zoneData: [
              ...quest.zoneData,
              {
                availablity_time_from: "",
                availablity_time_to: "",
                zone_id: zone.id,
                enabled: false,
                is_active: 0,
              },
            ],
          };
        }
        if (questionType === "weight") {
          setRangeData((prev) =>
            prev.map((itm) => ({
              ...itm,
              zoneData: [
                ...itm.zoneData,
                { price: "", zone_id: zone.id, enabled: false, is_active: 0 },
              ],
            }))
          );
          return {
            ...quest,
            rangeData: quest.rangeData.map((itm) => ({
              ...itm,
              zoneData: [
                ...itm.zoneData,
                { price: "", zone_id: zone.id, enabled: false, is_active: 0 },
              ],
            })),
          };
        }
        if (questionType === "size") {
          setDimensions((prev) =>
            prev.map((itm) => ({
              ...itm,
              zoneData: [
                ...itm.zoneData,
                { price: "", zone_id: zone.id, enabled: false, is_active: 0 },
              ],
            }))
          );
          return {
            ...quest,
            dimensions: quest.dimensions.map((itm) => ({
              ...itm,
              zoneData: [
                ...itm.zoneData,
                { price: "", zone_id: zone.id, enabled: false, is_active: 0 },
              ],
            })),
          };
        }
        if (questionType === "cod") {
          setCod((prev) =>
            prev.map((itm) => ({
              ...itm,
              zoneData: [
                ...itm.zoneData,
                { price: "", zone_id: zone.id, enabled: false, is_active: 0 },
              ],
            }))
          );
          return {
            ...quest,
            cod: quest.cod?.map((itm) => ({
              ...itm,
              zoneData: [
                ...itm.zoneData,
                { price: "", zone_id: zone.id, enabled: false, is_active: 0 },
              ],
            })),
          };
        }
        if (questionType === "protection") {
          setProtection((prev) =>
            prev.map((itm) => ({
              ...itm,
              zoneData: [
                ...itm.zoneData,
                { price: "", zone_id: zone.id, enabled: false, is_active: 0 },
              ],
            }))
          );
          return {
            ...quest,
            shipmentProtectionRange: quest.shipmentProtectionRange?.map(
              (itm) => ({
                ...itm,
                zoneData: [
                  ...itm.zoneData,
                  { price: "", zone_id: zone.id, enabled: false, is_active: 0 },
                ],
              })
            ),
          };
        }
        return quest;
      })
    );
    setSync(false);
    await handleTempSave(false);
    setNewZoneAdded(false);
  };

  // Initial fetch of questions
  useEffect(() => {
    getAllQuestions();
    if (window.innerWidth < 1600) setZonePerPage(3);
    if (window.innerWidth > 1600) setZonePerPage(3);
  }, []);

  useEffect(() => {
    if (questions.length) getTempSettings();
  }, [questions]);

  // Handle temp save, skipping initial render
  useEffect(() => {
    if (isInitialRender.current) {
      if (questions?.length > 23 && data?.length > 23 && !settingsLoader) {
        isInitialRender.current = false;
      }
      return;
    }
    if (
      questions?.length > 23 &&
      data?.length > 23 &&
      !settingsLoader &&
      focused === ""
    ) {
      if (zones.length === 0) {
        const timer = setTimeout(() => handleTempSave(), 1000);
        return () => clearTimeout(timer);
      } else {
        handleTempSave();
      }
    }
  }, [zones, data, rangeData, dimensions]);

  // Handle zone deletion
  useEffect(() => {
    if (zoneDeleted) {
      handleTempSave().then(() => {
        reloadMap();
        setZoneDeleted(false);
      });
    }
  }, [zoneDeleted, handleTempSave]);

  // Handle new zone addition
  useEffect(() => {
    if (newZoneAdded && questions?.length > 0) addNewZone(newZoneAdded);
  }, [newZoneAdded, questions]);

  return (
    <div className="w-full h-full relative px-2.5 py-0">
      {!settingsLoader && (
        <div className="w-full flex items-center justify-end">
          <LightTooltip
            placement="top"
            arrow
            title={
              sync === false ? t("zoneSettings.saveTheseSettings") : t("zoneSettings.noChangesToSave")
            }
          >
            <button
              onClick={handleSaveZoneSettings}
              disabled={sync === true || zones?.length === 0 || settingsLoader}
              className={`flex items-center gap-2 px-2.5 py-1.5 text-white text-sm rounded-md shadow-sm transition relative bottom-1 ${
                sync !== true && zones?.length > 0 && !settingsLoader
                  ? "bg-brand hover:bg-brand-hover cursor-pointer"
                  : "bg-ui-text-subtle cursor-not-allowed opacity-50"
              }`}
            >
              <MdSave className="w-4 h-4" />
              <span>{t("common:actions.save")}</span>
            </button>
          </LightTooltip>
        </div>
      )}
      {!settingsLoader && (
        <div className="zsettings_table_contentwrap h-[90%] overflow-auto">
          <div className="zsettings_table_content">
            <div className="zstable_content_left">
              <QuestionsCard
                questions={questions}
                activeQuestion={activeQuestion}
                hoverQuestion={hoverQuestion}
                data={data}
                setActiveQuestion={setActiveQuestion}
                setHoverQuestion={setHoverQuestion}
              />
              <SettingsCard
                questions={questions}
                activeQuestion={activeQuestion}
                hoverQuestion={hoverQuestion}
                rangeData={rangeData}
                dimensions={dimensions}
                cod={cod}
                protection={protection}
                data={data}
                setData={(newData) => {
                  setData(newData);
                  setSync(false);
                }}
                setDimensions={(newDimensions) => {
                  setDimensions(newDimensions);
                  setSync(false);
                }}
                setCod={(newCod) => {
                  setCod(newCod);
                  setSync(false);
                }}
                setProtection={(newProtection) => {
                  setProtection(newProtection);
                  setSync(false);
                }}
                expandDropdown={expandDropdown}
                setExpandDropdown={setExpandDropdown}
                setRangeData={(newRangeData) => {
                  setRangeData(newRangeData);
                  setSync(false);
                }}
                rangeDropdown={rangeDropdown}
                setRangeDropdown={setRangeDropdown}
              />
            </div>
            <div className="zstcontent_border_center mr-2"></div>
            <Zones
              data={data}
              rangeData={rangeData}
              dimensions={dimensions}
              cod={cod}
              protection={protection}
              setData={(newData) => {
                setData(newData);
                setSync(false);
              }}
              setRangeData={(newRangeData) => {
                setRangeData(newRangeData);
                setSync(false);
              }}
              setDimensions={(newDimensions) => {
                setDimensions(newDimensions);
                setSync(false);
              }}
              setCod={(newCod) => {
                setCod(newCod);
                setSync(false);
              }}
              setProtection={(newProtection) => {
                setProtection(newProtection);
                setSync(false);
              }}
              loadingField={loadingField}
              setLoadingField={setLoadingField}
              activeQuestion={activeQuestion}
              hoverQuestion={hoverQuestion}
              focused={focused}
              setFocused={setFocused}
              zones={zones}
              questions={questions}
              zonePerPage={zonePerPage}
              handleTempSave={handleTempSave}
              onDataChange={() => setSync(false)}
            />
          </div>
        </div>
      )}
      {settingsLoader && <Loader settingsLoader={settingsLoader} />}
    </div>
  );
};

export default ZoneSettings;
